import { type QueryKey, useQuery } from '@tanstack/react-query';
import * as React from 'react';
import type { TempoUserResponse } from '@/common/api/types/userManagement';
import { commonQueryKeys } from '@/common/queryKeys/common';
import { tempoUsersByIdsQueryOptions } from '@/features/settings/user-management/queries/tempoUserQueries';
import { userManagementQueryKeys } from '@/features/settings/user-management/queryKeys';

export type VisibleUsersData = {
  valuesById: ReadonlyMap<string, TempoUserResponse>;
  missingIds: ReadonlySet<string>;
};

const emptyData = (): VisibleUsersData => ({
  valuesById: new Map(),
  missingIds: new Set(),
});

export const visibleUsersQueryKey = (instanceKey: string): QueryKey =>
  [commonQueryKeys.itemsTable.visibleUsers, instanceKey] as const;

const arraysShallowEqual = (a: readonly string[], b: readonly string[]): boolean => {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
};

/**
 * Accumulating set-shaped bulk-fetch of users referenced by visible cells.
 *
 * Cells register the user ids they need; the provider debounces those into
 * `ids` and this hook fetches the missing ones, accumulating results into one
 * React Query cache entry per instance. Each fetched user is also mirrored to
 * `userManagementQueryKeys.users.byId(id)` so the rest of the app can read it.
 */
export const useVisibleUsersQuery = ({
  instanceKey,
  ids,
  enabled = true,
}: {
  instanceKey: string;
  ids: readonly string[];
  enabled?: boolean;
}) => {
  const queryKey = React.useMemo(() => visibleUsersQueryKey(instanceKey), [instanceKey]);
  const requestedIdsRef = React.useRef<readonly string[]>(ids);
  requestedIdsRef.current = ids;

  const query = useQuery<VisibleUsersData>({
    queryKey,
    queryFn: async ({ client }): Promise<VisibleUsersData> => {
      const requested = requestedIdsRef.current;
      const previous = client.getQueryData<VisibleUsersData>(queryKey) ?? emptyData();

      const valuesById = new Map(previous.valuesById);
      const missingIds = new Set(previous.missingIds);
      const idsNeeded: string[] = [];
      for (const id of requested) {
        if (valuesById.has(id) || missingIds.has(id)) continue;
        idsNeeded.push(id);
      }
      if (idsNeeded.length === 0) {
        return valuesById.size === previous.valuesById.size ? previous : { valuesById, missingIds };
      }

      const users = await client.fetchQuery(tempoUsersByIdsQueryOptions(idsNeeded));
      const fetched = new Map<string, TempoUserResponse>();
      for (const user of users) {
        fetched.set(user.id, user);
        client.setQueryData(userManagementQueryKeys.users.byId(user.id), user);
      }
      for (const [id, value] of fetched) valuesById.set(id, value);
      for (const id of idsNeeded) {
        if (!fetched.has(id)) missingIds.add(id);
      }
      return { valuesById, missingIds };
    },
    enabled: enabled && ids.length > 0,
    staleTime: Number.POSITIVE_INFINITY,
  });

  // refetch() during an in-flight fetch is coalesced by React Query, so we
  // stash the new ids and re-issue after the in-flight query settles.
  const lastRequestedRef = React.useRef<readonly string[]>(ids);
  const inFlightRequestedRef = React.useRef<readonly string[] | null>(null);
  const { isFetching, refetch } = query;
  React.useEffect(() => {
    if (!enabled) return;
    if (arraysShallowEqual(lastRequestedRef.current, ids)) return;
    lastRequestedRef.current = ids;
    if (ids.length === 0) return;
    if (isFetching) {
      inFlightRequestedRef.current = ids;
      return;
    }
    void refetch();
  }, [ids, enabled, refetch, isFetching]);

  React.useEffect(() => {
    if (!enabled || isFetching) return;
    const pending = inFlightRequestedRef.current;
    if (!pending) return;
    inFlightRequestedRef.current = null;
    if (!arraysShallowEqual(pending, lastRequestedRef.current)) return;
    void refetch();
  }, [enabled, isFetching, refetch]);

  return query;
};
